import { useState, useEffect, useRef, useCallback } from "react";
import {
  getConversationProjet,
  createCommentaireProjet,
  markConversationProjetLue,
} from "../services/api";

/**
 * The Crowd v2 — Fil de conversation d'un projet
 *
 * Copie v2 de components/ConversationProjet.jsx : meme logique (memes
 * appels API, marquage lu a l'ouverture, Entree pour envoyer,
 * Maj+Entree pour aller a la ligne), balisage et classes v2. Le
 * composant d'origine reste utilise par l'interface actuelle.
 *
 * Fil unifie : commentaires postes sur le projet et commentaires des
 * etapes, melanges chronologiquement. L'auto-mention (tous les acteurs
 * du projet) est geree cote backend.
 *
 * Props :
 * - projetId (int, requis)
 * - collaborateurId (int, requis) : utilisateur courant
 * - onNonLusChange (fn optionnelle) : appelee avec 0 une fois le fil
 *   ouvert et marque lu
 * - onMessageEnvoye (fn optionnelle) : appelee apres un envoi reussi,
 *   pour que la page rafraichisse l'apercu de la liste (ajout v2)
 */
export default function ConversationProjetV2({ projetId, collaborateurId, onNonLusChange, onMessageEnvoye }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [texte, setTexte] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const listRef = useRef(null);

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur(null);
    try {
      const data = await getConversationProjet(projetId, collaborateurId);
      setMessages(data.messages || []);
    } catch (e) {
      setErreur(e.message || "Impossible de charger la conversation.");
    } finally {
      setLoading(false);
    }
  }, [projetId, collaborateurId]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Marquer comme lu a l'ouverture, une fois le fil charge
  useEffect(() => {
    if (!loading && !erreur) {
      markConversationProjetLue(projetId, collaborateurId)
        .then(() => onNonLusChange && onNonLusChange(0))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const envoyer = async () => {
    if (!texte.trim() || envoi) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await createCommentaireProjet(projetId, texte.trim(), collaborateurId);
      setTexte("");
      await charger();
      if (onMessageEnvoye) onMessageEnvoye();
    } catch (e) {
      setErreur(e.message || "Envoi impossible.");
    } finally {
      setEnvoi(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      envoyer();
    }
  };

  const formatHeure = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="v2-fil">
      <div className="v2-fil-liste" ref={listRef} aria-live="polite">
        {loading && <div className="v2-fil-info">Chargement…</div>}
        {erreur && !loading && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
        {!loading && !erreur && messages.length === 0 && (
          <div className="v2-fil-info">Aucun message pour l'instant.</div>
        )}
        {!loading && messages.map((m) => (
          <article key={`${m.source_type}-${m.id}`} className="v2-fil-message">
            <header className="v2-fil-message-entete">
              <span className="v2-fil-auteur">{m.auteur_nom}</span>
              {m.source_type === "etape" && <span className="v2-badge">{m.etape_nom}</span>}
              <span className="v2-fil-date">{formatHeure(m.created_at)}</span>
            </header>
            <div className="v2-fil-contenu">{m.contenu}</div>
          </article>
        ))}
      </div>

      <div className="v2-fil-saisie">
        <label htmlFor={`v2-fil-texte-${projetId}`} className="v2-invisible">Message</label>
        <textarea
          id={`v2-fil-texte-${projetId}`}
          className="v2-fil-texte"
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Écrire un message… (Entrée pour envoyer, Maj+Entrée pour aller à la ligne)"
          rows={2}
          disabled={envoi}
        />
        <button
          type="button"
          className="v2-btn v2-btn-principal"
          onClick={envoyer}
          disabled={!texte.trim() || envoi}
        >
          {envoi ? "Envoi…" : "Envoyer"}
        </button>
      </div>
    </div>
  );
}
